import type {
  Condition,
  ConditionGroup,
  ConditionOperator,
  PatientDataField,
} from './condition.schemas';
import {
  getOperatorRestrictions,
  getPatientDataFieldOperators,
} from './operator-restrictions';
import { genderValues, CHRONIC_ILLNESSES } from '../participant';
import { z } from 'zod';
import type { QuestionParameters } from '../questionnaire';

export type QuestionForValidation = {
  order: number;
  label: string;
  id: string;
  parameters: QuestionParameters;
  displayConditions?: ConditionGroup | null;
};

export function validateConditions(
  questions: QuestionForValidation[],
  ctx: z.RefinementCtx,
) {
  for (const [index, question] of questions.entries()) {
    validateConditionGroup(question, questions, index, ctx);
  }
}

function validateConditionGroup(
  question: QuestionForValidation,
  questions: QuestionForValidation[],
  questionIndex: number,
  ctx: z.RefinementCtx,
): void {
  if (!question.displayConditions?.conditions?.length) {
    return;
  }

  for (const [
    conditionIndex,
    condition,
  ] of question.displayConditions.conditions.entries()) {
    validateConditionWithTarget(
      condition,
      question,
      questions,
      questionIndex,
      conditionIndex,
      ctx,
    );
  }
}

function validateConditionWithTarget(
  condition: Condition,
  question: QuestionForValidation,
  questions: QuestionForValidation[],
  questionIndex: number,
  conditionIndex: number,
  ctx: z.RefinementCtx,
): void {
  if (condition.target.source === 'question') {
    validateQuestionCondition(
      condition,
      question,
      questions,
      questionIndex,
      conditionIndex,
      ctx,
    );
  } else if (condition.target.source === 'patientData') {
    validatePatientDataCondition(
      condition,
      condition.target.field,
      questionIndex,
      conditionIndex,
      ctx,
    );
  }
}

function validateQuestionCondition(
  condition: Condition,
  question: QuestionForValidation,
  questions: QuestionForValidation[],
  questionIndex: number,
  conditionIndex: number,
  ctx: z.RefinementCtx,
): void {
  if (condition.target.source !== 'question') {
    return;
  }

  const path = (fields: (string | number)[] = []) => [
    questionIndex,
    'displayConditions',
    'conditions',
    conditionIndex,
    ...fields,
  ];

  const targetId = condition.target.questionId;
  const targetQuestion = questions.find((q) => q.id === targetId);
  if (!targetQuestion) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: path(['target', 'questionId']),
      message: `La question cible ${targetId} n'existe pas`,
    });
    return;
  }

  // TODO : use order instead of index
  const targetIndex = questions.findIndex((q) => q.id === targetId);

  if (questionIndex <= targetIndex) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: path(['target', 'questionId']),
      message: `La question cible ${targetQuestion.label} doit apparaître avant la question ${question.label}`,
    });
  }

  validateConditionAgainstQuestionTarget(condition, targetQuestion, path, ctx);
}

export function validateConditionAgainstQuestionTarget(
  condition: Condition,
  targetQuestion: QuestionForValidation,
  path: (fields?: (string | number)[]) => (string | number)[],
  ctx: z.RefinementCtx,
): void {
  const questionType = targetQuestion.parameters.type;
  const allowedOperators = getOperatorRestrictions(
    questionType,
    targetQuestion.parameters,
  );

  if (!allowedOperators.includes(condition.operator)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: path(['operator']),
      message: `Operator "${condition.operator}" is not valid for ${questionType} questions. Allowed operators: ${allowedOperators.join(', ')}`,
    });
    return;
  }

  if (requiresRange(condition.operator)) {
    if (!('minValue' in condition) || isValueMissing(condition.minValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['minValue']),
        message: 'Minimum value is required for between operator',
      });
    }
    if (!('maxValue' in condition) || isValueMissing(condition.maxValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['maxValue']),
        message: 'Maximum value is required for between operator',
      });
    }
  }

  if (requiresValue(condition.operator)) {
    if (!('value' in condition) || isValueMissing(condition.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['value']),
        message: 'Value is required for this operator',
      });
      return;
    }

    if (questionType === 'number' && typeof condition.value !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['value']),
        message: 'Value must be a number for numeric questions',
      });
    }

    if (isChoiceQuestion() && typeof condition.value === 'string') {
      if ('options' in targetQuestion.parameters) {
        const validOptions = targetQuestion.parameters.options.map(
          (opt) => opt.label,
        );
        if (!validOptions.includes(condition.value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: path(['value']),
            message: `Value must be one of: ${validOptions.join(', ')}`,
          });
        }
      }
    }
  }

  function isChoiceQuestion() {
    return (
      questionType === 'radio' ||
      questionType === 'select' ||
      questionType === 'checkboxes'
    );
  }
}

function validatePatientDataCondition(
  condition: Condition,
  field: PatientDataField,
  questionIndex: number,
  conditionIndex: number,
  ctx: z.RefinementCtx,
): void {
  const path = (fields: string[]) => [
    questionIndex,
    'displayConditions',
    'conditions',
    conditionIndex,
    ...fields,
  ];

  const allowedOperators = getPatientDataFieldOperators(field);

  if (!allowedOperators.includes(condition.operator)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: path(['operator']),
      message: `Operator "${condition.operator}" is not valid for patient data field "${field}". Allowed operators: ${allowedOperators.join(', ')}`,
    });
    return;
  }

  if (requiresRange(condition.operator)) {
    if (!('minValue' in condition) || isValueMissing(condition.minValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['minValue']),
        message: 'Minimum value is required for between operator',
      });
    }
    if (!('maxValue' in condition) || isValueMissing(condition.maxValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['maxValue']),
        message: 'Maximum value is required for between operator',
      });
    }

    if (
      field === 'birthdate' &&
      'minValue' in condition &&
      'maxValue' in condition
    ) {
      const minDate = new Date(condition.minValue as string);
      const maxDate = new Date(condition.maxValue as string);
      if (minDate > maxDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: path(['minValue']),
          message: 'Minimum date must be before maximum date',
        });
      }
    }
  }

  if (requiresValue(condition.operator)) {
    if (!('value' in condition) || isValueMissing(condition.value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: path(['value']),
        message: 'Value is required for this operator',
      });
      return;
    }

    if (field === 'gender' && typeof condition.value === 'string') {
      const validGenderValues = genderValues as readonly string[];
      if (!validGenderValues.includes(condition.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: path(['value']),
          message: `Value must be one of: ${genderValues.join(', ')}`,
        });
      }
    }

    if (field === 'chronicIllnesses' && typeof condition.value === 'string') {
      const validIllnesses = CHRONIC_ILLNESSES as readonly string[];
      if (!validIllnesses.includes(condition.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: path(['value']),
          message: `Value must be one of the valid chronic illnesses`,
        });
      }
    }

    if (field === 'birthdate' && typeof condition.value === 'string') {
      const date = new Date(condition.value);
      if (isNaN(date.getTime())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: path(['value']),
          message: 'Value must be a valid date',
        });
      }
    }
  }
}

const OPERATORS_WITHOUT_VALUE: ConditionOperator[] = [
  'is_empty',
  'is_not_empty',
];
const OPERATORS_WITH_RANGE: ConditionOperator[] = ['between'];

export function requiresValue(operator: ConditionOperator): boolean {
  return (
    !OPERATORS_WITHOUT_VALUE.includes(operator) &&
    !OPERATORS_WITH_RANGE.includes(operator)
  );
}

export function requiresRange(operator: ConditionOperator): boolean {
  return OPERATORS_WITH_RANGE.includes(operator);
}

export function isValueMissing(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}
